import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Input, Modal, ModalBody, Alert } from "reactstrap";
import { formatISODate } from "../../../../../../actions/util";
import {
  CHECK_CONFIRM,
  CHECK_SUCCESS,
  CHECK_WARNING,
} from "../../../../../../components/alert/Alert";
import {
  BoSungVanDesEndpoint,
  KetQuaBieuQuyetEndpoint,
} from "../../../../../../service/Meet";
import {
  Enums_Mau,
  _Enum_Trang_Thai_Van_De,
} from "../../../../../action/defaultEnums";
import { useAsync } from "../../../../../../hooks";
import TeamPending from "./Pending";
import "./style.scss";
import { isBefore, differenceInSeconds } from "date-fns";
import { TypeEnums } from "../../reloadConstants";
import { actionGRPC } from "../../../Redux/ActionsMeeting";
import { get } from "lodash";
import { Icon, Stack } from "@mui/material";

const INIT_OTHER = "YKIENKHAC";

// Status configuration
const STATUS_CONFIG = {
  [_Enum_Trang_Thai_Van_De.HoanThanh]: {
    color: "#10b981",
    bgColor: "#d1fae5",
    label: "Hoàn thành",
    icon: "check-circle",
  },
  [_Enum_Trang_Thai_Van_De.HoatDong]: {
    color: "#3b82f6",
    bgColor: "#dbeafe",
    label: "Đang thảo luận",
    icon: "comments",
  },
  [_Enum_Trang_Thai_Van_De.Moi]: {
    color: "#f59e0b",
    bgColor: "#fef3c7",
    label: "Chờ xử lý",
    icon: "clock",
  },
};

// Countdown Timer Component
const CountdownTimer = ({ endDate, startDate }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!endDate || !startDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const end = new Date(endDate);
      const diff = differenceInSeconds(end, now);

      if (diff <= 0) return null;

      const minutes = Math.floor(diff / 60);
      const seconds = diff % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      const time = calculateTimeLeft();
      setTimeLeft(time);
      if (!time) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate, startDate]);

  if (!timeLeft) return null;

  return (
    <div className="voting-timer-badge">
      <div className="timer-icon">
        <Icon>access_alarm</Icon>
      </div>
      <div className="timer-content">
        <span className="timer-label">Thời gian còn lại</span>
        <span className="timer-value">{timeLeft}</span>
      </div>
    </div>
  );
};

// Voting Option Card Component
const VotingOptionCard = ({
  option,
  isSelected,
  isDisabled,
  onClick,
  variant = "default",
  children,
}) => {
  const variants = {
    approve: {
      icon: "check_circle",
      color: "#10b981",
      bgColor: "#10b981",
      bgColorHover: "#059669",
      label: "Tán thành",
      description: "Đồng ý thông qua dự thảo",
    },
    reject: {
      icon: "highlight_off",
      color: "#ef4444",
      bgColor: "#ef4444",
      bgColorHover: "#dc2626",
      label: "Không tán thành",
      description: "Từ chối dự thảo này",
    },
    other: {
      icon: "sms",
      color: "#6b7280",
      bgColor: "#fff",
      bgColorHover: "#f3f4f6",
      label: "Ý kiến khác",
      description: "Gửi phản hồi hoặc ý kiến riêng",
    },
    default: {
      icon: "receipt_long",
      color: "#6b7280",
      bgColor: "#fff",
      bgColorHover: "#f3f4f6",
      label: option?.noiDung || "",
      description: "",
    },
  };

  const config = variants[variant] || variants.default;
  const isColoredVariant = variant === "approve" || variant === "reject";

  return (
    <div
      className={`voting-option-card ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""} ${isColoredVariant && !isDisabled ? "colored" : ""}`}
      style={{
        "--card-color": config.color,
        "--card-bg":
          isColoredVariant && !isDisabled ? config.bgColor : config.bgColor,
        "--card-bg-hover": config.bgColorHover,
      }}
      onClick={() => !isDisabled && onClick?.()}
    >
      <div
        className={`option-icon ${isColoredVariant && !isDisabled ? "white-icon" : ""}`}
      >
        <Icon>{config.icon}</Icon>
      </div>
      <div className="option-content">
        <span className="option-label">{config.label}</span>
        {config.description && (
          <span className="option-description">{config.description}</span>
        )}
        {children}
        {option?.yKienKhac && (
          <div className="other-opinion-badge">
            <Icon>sms</Icon>
            <span>Ý kiến khác</span>
          </div>
        )}
      </div>
      {isSelected && (
        <div className="option-check">
          <Icon>check_circle</Icon>
        </div>
      )}
    </div>
  );
};

export default function ModalDanhGia(props) {
  const { boSungVanDeId, isOpen, isClose, isDienBien, idThanhPhanThamDu } =
    props;
  const [textYKien, setTextYKien] = useState("");
  const [dataTraLoi, setDataTraLoi] = useState({});
  const { noiDung, thamDus } = useSelector((state) => state.meetingRProcess);
  const [dataPhuongAns, setDataPhuongAns] = useState([]);
  const idKetQuaBieuQuyet = useRef(null);
  const [vanDeBieuQuyet, setVanDeBieuQuyet] = useState(null);
  const { grpc } = useSelector((state) => state.meetingRProcess);
  const dispatch = useDispatch();

  // Get current user name
  const currentUserName = useMemo(() => {
    if (!thamDus?.thamGia || !idThanhPhanThamDu) return "Người dùng";
    const currentUser = thamDus.thamGia.find((t) => t.id === idThanhPhanThamDu);
    const hoVaTenDem = get(
      currentUser,
      "nhanSu.thongTinCaNhan.hoVaTenDem",
      "Người dùng",
    );
    const ten = get(currentUser, "nhanSu.thongTinCaNhan.ten", "Người dùng");
    return hoVaTenDem ? `${hoVaTenDem} ${ten}` : "Cá nhân";
  }, [thamDus, idThanhPhanThamDu]);

  // Count voted users
  const votedCount = useMemo(() => {
    return {
      voted: dataPhuongAns.reduce(
        (acc, pa) => acc + (pa.soLuongBieuQuyet || 0),
        0,
      ),
      total:
        thamDus?.thamGia?.filter((t) => t.trangThaiThamDu === "ThamGia")
          ?.length || 0,
    };
  }, [dataPhuongAns, thamDus]);

  const getTraLoi = useCallback(
    async (boSungVanDeId, isReloadAnswer = true) => {
      try {
        const boSungVanDe =
          await BoSungVanDesEndpoint.getVanDesById(boSungVanDeId);
        const {
          value: [answer],
        } = await KetQuaBieuQuyetEndpoint.getKetQuaById(
          boSungVanDeId,
          idThanhPhanThamDu,
        );
        if (answer) {
          idKetQuaBieuQuyet.current = answer?.id;
          isReloadAnswer &&
            setDataTraLoi({
              ...(answer.ngayTraLoi
                ? { ...answer.phuongAnBoSung, answered: true }
                : {}),
            });
          setDataPhuongAns(boSungVanDe.phuongAnBoSungs);
          setVanDeBieuQuyet(boSungVanDe);
        } else {
          idKetQuaBieuQuyet.current = "notAllow";
        }
        return true;
      } catch (error) {
        CHECK_WARNING(error);
      }
    },
    [idThanhPhanThamDu],
  );

  const { execute: reloadVanDe, status } = useAsync(getTraLoi, false);

  useEffect(() => {
    if (boSungVanDeId) {
      reloadVanDe(boSungVanDeId);
    }
  }, [boSungVanDeId, reloadVanDe]);

  const reloadData = useCallback(
    (_grpc) => {
      if (!isOpen) return;
      const { data } = _grpc;
      if (data.type === TypeEnums.KETQUA) {
        getTraLoi(data.key, false);
        dispatch(actionGRPC({ ..._grpc, isViewed: true }));
      }
    },
    [dispatch, getTraLoi, isOpen],
  );

  useEffect(() => {
    if (grpc && !grpc.isViewed) {
      reloadData(grpc);
    }
  }, [grpc, reloadData]);

  const sendKQ = useCallback(() => {
    if (!dataTraLoi?.id) {
      CHECK_WARNING("Vui lòng chọn phương án");
      return false;
    } else {
      if (dataTraLoi.id === INIT_OTHER && !textYKien)
        return CHECK_WARNING("Vui lòng nhập ý kiến");
      CHECK_CONFIRM("Bạn có chắc chắn chọn kết quả này?").then((res) => {
        if (res) {
          if (dataTraLoi.id === INIT_OTHER) {
            const modelPhuongAnBoSung = {
              thuTu: dataPhuongAns.length + 1,
              noiDung: textYKien,
              boSungVanDeId: boSungVanDeId,
              isBieuQuyet: true,
            };
            BoSungVanDesEndpoint.createOtherOption(modelPhuongAnBoSung)
              .then((_res) => {
                isClose();
                CHECK_SUCCESS("Gửi kết quả thành công");
              })
              .catch((err) => console.log("lỗi gửi kết quả"));
          } else {
            const model = {
              phuongAnBoSungId: dataTraLoi.id,
              ngayTraLoi: formatISODate(new Date()),
            };
            KetQuaBieuQuyetEndpoint.updateKetQuaBieuQuyet(
              idKetQuaBieuQuyet.current,
              model,
            )
              .then((rest) => {
                isClose();
                CHECK_SUCCESS("Gửi kết quả thành công");
              })
              .catch((err) => console.log("lỗi gửi kết quả"));
          }
        }
      });
    }
  }, [dataTraLoi, textYKien, dataPhuongAns, boSungVanDeId, isClose]);

  const handleChange = (value) => {
    if (value === INIT_OTHER) return setDataTraLoi({ id: value, value });
    setDataTraLoi(value);
  };

  const { trangThai: trangThaiNoiDung } = noiDung || {};

  function checkExpired(vanDeBieuQuyet) {
    const { ngayKetThuc, ngayBatDau } = vanDeBieuQuyet || {};
    if (!ngayBatDau) return false;
    return isBefore(new Date(ngayKetThuc), new Date());
  }

  const isDisabled = useMemo(() => {
    return (
      checkExpired(vanDeBieuQuyet) ||
      vanDeBieuQuyet?.trangThai === "HoanThanh" ||
      isDienBien ||
      dataTraLoi?.answered ||
      trangThaiNoiDung === "HoanThanh"
    );
  }, [vanDeBieuQuyet, isDienBien, dataTraLoi?.answered, trangThaiNoiDung]);

  const mau = vanDeBieuQuyet
    ? Enums_Mau.find((f) => f.value === vanDeBieuQuyet.mau)
    : {};

  const statusConfig =
    STATUS_CONFIG[vanDeBieuQuyet?.trangThai] ||
    STATUS_CONFIG[_Enum_Trang_Thai_Van_De.Moi];

  // Determine option variants based on content
  const getOptionVariant = (option, index) => {
    const content = option.noiDung?.toLowerCase() || "";
    if (option?.yKienKhac) {
      return "default";
    }
    if (content.includes("không")) return "reject";
    if (
      content.includes("đồng ý") ||
      content.includes("tán thành") ||
      content.includes("thông qua")
    ) {
      return "approve";
    }
    if (
      content.includes("không đồng ý") ||
      content.includes("không tán thành") ||
      content.includes("từ chối")
    ) {
      return "reject";
    }
    return "default";
  };
  return (
    <Modal
      className="voting-modal-modern"
      size="md"
      isOpen={isOpen ? true : false}
      centered
    >
      {/* Custom Header */}
      <div className="voting-modal-header">
        <button className="header-close-btn" onClick={isClose}>
          <Icon>undo</Icon>
        </button>
        <h5 className="header-title">Biểu quyết</h5>
        <div className="header-spacer" />
      </div>

      <ModalBody className="voting-modal-body">
        {idKetQuaBieuQuyet.current && (
          <>
            {vanDeBieuQuyet && (
              <TeamPending loading={status === "pending"}>
                {/* Status Badges Row */}
                <div className="status-badges-row">
                  <CountdownTimer
                    endDate={vanDeBieuQuyet.ngayKetThuc}
                    startDate={vanDeBieuQuyet.ngayBatDau}
                  />
                  <div className="voted-count-badge">
                    <div
                      className="voted-icon"
                      style={{ backgroundColor: "#fff4e5", color: "#f97316" }}
                    >
                      <Icon>person</Icon>
                    </div>
                    <div className="voted-content">
                      <span className="voted-label">Loại biểu quyết</span>
                      <span className="voted-value">
                        {vanDeBieuQuyet.loai === "CongKhai"
                          ? "Công khai"
                          : "Kín"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Issue Info Card */}
                <div className="issue-info-card">
                  <div className="issue-badge">
                    {mau?.label || "Biểu quyết"} • {statusConfig.label}
                  </div>
                  <h4 className="issue-title">{vanDeBieuQuyet.name}</h4>
                  {vanDeBieuQuyet.moTa && (
                    <p className="issue-description">{vanDeBieuQuyet.moTa}</p>
                  )}
                </div>

                {/* Voting Options Section */}
                <div className="voting-options-section">
                  <h6 className="options-title">Lựa chọn của bạn</h6>
                  <p className="options-subtitle">
                    Vui lòng chọn một trong các phương án bên dưới
                  </p>

                  <div className="voting-options-list">
                    {dataPhuongAns.map((item, index) => {
                      const variant = getOptionVariant(item, index);
                      return (
                        <VotingOptionCard
                          key={item.id}
                          option={item}
                          variant={variant}
                          isSelected={dataTraLoi.id === item.id}
                          isDisabled={isDisabled}
                          onClick={() => handleChange(item)}
                        />
                      );
                    })}

                    {/* Other Option */}
                    {!dataTraLoi.answered && !checkExpired(vanDeBieuQuyet) && (
                      <VotingOptionCard
                        variant="other"
                        isSelected={dataTraLoi.id === INIT_OTHER}
                        isDisabled={
                          vanDeBieuQuyet.trangThai === "HoanThanh" ||
                          isDienBien ||
                          trangThaiNoiDung === "HoanThanh"
                        }
                        onClick={() => handleChange(INIT_OTHER)}
                      >
                        {dataTraLoi.id === INIT_OTHER && (
                          <Input
                            autoFocus
                            type="textarea"
                            className="other-input"
                            placeholder="Vui lòng nhập ý kiến của bạn..."
                            value={textYKien}
                            onChange={(e) => setTextYKien(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </VotingOptionCard>
                    )}
                  </div>
                </div>

                {/* User Status Footer */}
                <div className="user-status-footer">
                  <div className="user-status-icon">
                    <Icon>
                      {dataTraLoi.answered ? "check_circle" : "account_circle"}
                    </Icon>
                  </div>
                  <span className="user-status-text">
                    {dataTraLoi.answered
                      ? `Bạn đã biểu quyết với tư cách đại biểu tham dự: ${currentUserName}`
                      : `Bạn đang biểu quyết với tư cách đại biểu tham dự: ${currentUserName}`}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="voting-action-buttons">
                  <Button className="btn-cancel" onClick={isClose}>
                    <Icon>highlight_off</Icon>
                    <span>Đóng</span>
                  </Button>
                  {vanDeBieuQuyet.trangThai !== "HoanThanh" &&
                    !isDienBien &&
                    !dataTraLoi.answered && (
                      <Button className="btn-submit" onClick={sendKQ}>
                        <Icon>send</Icon>
                        <span>Gửi kết quả</span>
                      </Button>
                    )}
                </div>

                {/* Answered Stamp */}
                {(isDienBien ||
                  vanDeBieuQuyet.trangThai === "HoanThanh" ||
                  dataTraLoi?.answered ||
                  trangThaiNoiDung === "HoanThanh") && (
                  <div
                    className={`voting-stamp ${dataTraLoi.answered ? "answered" : "not-answered"}`}
                  >
                    {dataTraLoi.answered ? "ĐÃ TRẢ LỜI" : "CHƯA TRẢ LỜI"}
                  </div>
                )}
              </TeamPending>
            )}
          </>
        )}
        {idKetQuaBieuQuyet.current === "notAllow" && (
          <Alert color="warning" className="not-allowed-alert">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon color="error">do_not_disturb_on</Icon>
              <span> Bạn không được quyền tham gia biểu quyết vấn đề này</span>
            </Stack>
          </Alert>
        )}
      </ModalBody>
    </Modal>
  );
}
